import { rollD6 } from '../utils/dice.js';
import { addRefugees, createImmatureWorker } from './workerSystem.js';
import { CONFIG } from '../config/constants.js';

/**
 * Applies immediate event effects
 * @param {Object} event - The event object
 * @param {Object} gameState - Current game state
 * @returns {Object} Updated game state
 */
export function applyEventEffect(event, gameState) {
  if (!event || event.type === 'quiet') {
    return gameState;
  }

  const handler = EVENT_HANDLERS[event.type];
  if (!handler) {
    return gameState;
  }

  return handler(event, gameState);
}

// Event handlers
const EVENT_HANDLERS = {
  spoilage: (event, state) => {
    const hasGranary = state.buildings.some(b => b.type === 'granary' && b.complete);
    const spoilAmount = hasGranary ? Math.ceil(rollD6() / 2) : rollD6();

    return {
      ...state,
      food: Math.max(0, state.food - spoilAmount),
      message: `Lost ${spoilAmount} food to spoilage.`
    };
  },

  traveler: (event, state) => {
    return {
      ...state,
      tempWorker: { id: 'temp', status: 'healthy' }
    };
  },

  warning: (event, state) => {
    return {
      ...state,
      raidScheduledForDay: state.day + 1, // Raid happens NEXT day
      raidWarning: true // Keep for compatibility
    };
  },

  raid: (event, state) => {
    // Raid happens at end of THIS day (immediate)
    return {
      ...state,
      raidScheduledForDay: state.day, // Raid happens THIS day
      raidWarning: true // Keep for compatibility
    };
  },

  storm: (event, state) => state,
  weather: (event, state) => state,
  bounty: (event, state) => state,
  quiet: (event, state) => state,

  // New event variant handlers
  refugees: (event, state) => {
    const count = Math.min(rollD6(), 3);
    const newWorkers = addRefugees(state.workers, count, state.day);
    return {
      ...state,
      workers: newWorkers,
      food: Math.max(0, state.food - 10),
      morale: Math.max(0, (state.morale || 50) - 5),
      message: `${count} refugees joined! They consumed 10 food.`
    };
  },

  discovery: (event, state) => {
    const metalFound = rollD6();
    return {
      ...state,
      metal: (state.metal || 0) + metalFound,
      message: `Found ${metalFound} metal!`
    };
  },

  festival: (event, state) => {
    return {
      ...state,
      morale: Math.min(100, (state.morale || 50) + 15),
      festivalDay: true, // Skip work assignments
      message: 'Your people celebrate! No work today, but spirits are high.'
    };
  },

  scholar: (event, state) => {
    const knowledgeGained = rollD6() + rollD6();
    return {
      ...state,
      knowledge: Math.min(CONFIG.KNOWLEDGE_CAP, (state.knowledge || 0) + knowledgeGained),
      message: `Scholar shared ${knowledgeGained} knowledge!`
    };
  },

  merchant: (event, state) => {
    // This would trigger a choice modal in the UI
    return {
      ...state,
      pendingTrade: {
        give: { food: 10 },
        receive: { wood: 5, stone: 3 }
      },
      message: 'A merchant offers to trade: 10 food for 5 wood + 3 stone.'
    };
  },

  birth: (event, state) => {
    const aliveCount = state.workers.filter(w => w.status !== 'dead').length;
    if (aliveCount < 5) {
      return {
        ...state,
        message: 'Your settlement is too small for a birth.'
      };
    }

    const hasNursery = state.buildings.some(b => b.type === 'nursery' && b.complete);
    const maturityDays = hasNursery ? 3 : 5;

    const newWorker = createImmatureWorker(
      state.workers.length + 1,
      state.day,
      maturityDays
    );

    return {
      ...state,
      workers: [...state.workers, newWorker],
      morale: Math.min(100, (state.morale || 50) + 10),
      message: `A child was born! They will mature in ${maturityDays} days.`
    };
  },

  breakthrough: (event, state) => {
    if (!state.buildings.some(b => b.type === 'workshop' && b.complete)) {
      return {
        ...state,
        message: 'No breakthrough without a workshop.'
      };
    }

    const techGained = rollD6();
    return {
      ...state,
      techPoints: (state.techPoints || 0) + techGained,
      message: `Research breakthrough! +${techGained} tech points.`
    };
  },

  golden_age: (event, state) => {
    return {
      ...state,
      goldenAgeDay: true, // Adds +3 to all tasks
      morale: Math.min(100, (state.morale || 50) + 15),
      message: 'A golden age dawns! All tasks receive +3 today.'
    };
  },

  divine_favor: (event, state) => {
    // Trigger choice modal
    return {
      ...state,
      pendingDivineFavor: true,
      message: 'Choose your divine gift: 20 food, 10 knowledge, 5 metal, or heal all workers.'
    };
  },

  traveler_choice: (event, state) => {
    // Don't auto-apply temp worker, let choice handler decide
    return state;
  }
};

/**
 * Checks if a birth occurs this day
 * @param {Object} gameState - Current game state
 * @returns {Object|null} Birth event object or null
 */
export function checkBirth(gameState) {
  const aliveWorkers = gameState.workers.filter(w => w.status !== 'dead');

  // Need minimum population for births
  if (aliveWorkers.length < 5) return null;

  // Calculate birth chance based on buildings
  const hasNursery = gameState.buildings.some(b => b.type === 'nursery' && b.complete);
  const birthChance = hasNursery ? 30 : 10; // 10% base, +20% with nursery

  // Roll 1d100 for birth check
  const roll = Math.floor(Math.random() * 100) + 1;

  if (roll <= birthChance) {
    const maturityDays = hasNursery ? 3 : 5;
    const maxId = Math.max(...gameState.workers.map(w => typeof w.id === 'number' ? w.id : 0));
    const newWorker = createImmatureWorker(maxId + 1, gameState.day, maturityDays);

    return {
      type: 'birth',
      name: 'Birth',
      icon: '👶',
      message: `A child was born! They will mature in ${maturityDays} days.`,
      worker: newWorker,
      moraleBonus: 10
    };
  }

  return null;
}
